import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase/config';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { Calendar as CalIcon, FolderOpen, Users } from 'lucide-react';
import MessageBoard from '../Messaging/MessageBoard';
import FileVault from '../Storage/FileVault';

const LawyerDashboard: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'availability' | 'my-clients'>('my-clients');
  const [date, setDate] = useState(new Date());
  const [myClients, setMyClients] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    if (!user) return;
    setLoading(true);
    
    // Fetch my assigned clients
    const myClientsSnap = await getDocs(query(collection(db, 'users'), where('assignedLawyerId', '==', user.uid)));
    setMyClients(myClientsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as any)));

    setLoading(false);
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading Dashboard...</div>;

  return (
    <div className="container" style={{ padding: '40px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1 style={{ color: '#0f172a' }}>Lawyer Legal Portal</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => setActiveTab('my-clients')} className={`btn ${activeTab === 'my-clients' ? 'btn-primary' : ''}`} style={{ border: '1px solid #ddd' }}>My Clients</button>
          <button onClick={() => setActiveTab('availability')} className={`btn ${activeTab === 'availability' ? 'btn-primary' : ''}`} style={{ border: '1px solid #ddd' }}>Availability</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selectedClient ? '1fr 2fr' : '1fr', gap: '30px' }}>
        
        {/* Left Column: Client Lists */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {activeTab === 'my-clients' && (
            <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: 'var(--shadow)' }}>
              <h3 style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Users size={20} /> Assigned Clients
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {myClients.map(client => (
                  <div 
                    key={client.id} 
                    onClick={() => setSelectedClient(client)}
                    style={{
                      padding: '15px',
                      borderRadius: '8px',
                      border: `2px solid ${selectedClient?.id === client.id ? 'var(--secondary-color)' : '#f0f0f0'}`,
                      cursor: 'pointer',
                      transition: 'var(--transition)'
                    }}
                  >
                    <div style={{ fontWeight: 'bold' }}>{client.email}</div>
                    <div style={{ fontSize: '0.75rem', color: '#999' }}>Client since {new Date(client.createdAt).toLocaleDateString()}</div>
                    <Link to={`/profile/${client.id}`} onClick={(event) => event.stopPropagation()} style={{ display: 'inline-block', marginTop: '10px', color: 'var(--secondary-color)', fontSize: '0.85rem', fontWeight: 600 }}>
                      Open Profile
                    </Link>
                  </div>
                ))}
                {myClients.length === 0 && <p style={{ color: '#999', fontSize: '0.9rem' }}>No clients assigned to you yet.</p>}
              </div>
            </div>
          )}

          {activeTab === 'availability' && (
            <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: 'var(--shadow)' }}>
              <h3 style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <CalIcon size={20} /> My Schedule
              </h3>
              <Calendar onChange={(v: any) => setDate(v)} value={date} className="lawyer-cal" />
              <p style={{ color: '#777', fontSize: '0.9rem', marginTop: '20px' }}>Availability is configured by Admin in the schedules tab.</p>
            </div>
          )}
        </div>

        {/* Right Column: Communication and Files */}
        {selectedClient && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
            <div style={{ display: 'flex', gap: '20px' }}>
              <div style={{ flex: 1 }}><MessageBoard clientId={selectedClient.id} lawyerId={user!.uid} /></div>
              <div style={{ flex: 1 }}><FileVault targetUserId={selectedClient.id} /></div>
            </div>
          </div>
        )}

        {!selectedClient && activeTab !== 'availability' && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9f9f9', borderRadius: '12px', border: '2px dashed #eee', minHeight: '400px' }}>
            <div style={{ textAlign: 'center', color: '#999' }}>
              <FolderOpen size={48} style={{ marginBottom: '15px', opacity: 0.5 }} />
              <p>Select a client from your list to view their files and communication.</p>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .lawyer-cal { width: 100% !important; border: none !important; }
        .react-calendar__tile--active { background: var(--primary-color) !important; border-radius: 4px; }
      `}</style>
    </div>
  );
};

export default LawyerDashboard;
